import { useCallback, useSyncExternalStore } from "react";
import { useToast } from "../../../shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../state/editor/useEditor";
import { pickFile } from "../../../util/download";
import { SCENE_FILE_ACCEPT } from "../../../util/sceneFileSniff";
import { useCaps } from "../CapsProvider";
import {
  describeImport,
  exportRingWithScene,
  formatBytes,
  openSceneFile,
  saveSceneFile,
  sceneBusy,
  subscribeSceneBusy,
  withSceneBusy,
  type SceneBusy,
  type SceneMessage,
} from "./sceneFile";

/** The scene-file action running on this viewer, re-rendering on every change. */
export function useSceneBusy(): SceneBusy {
  const { client } = useViewer();
  return useSyncExternalStore(
    useCallback((cb: () => void) => subscribeSceneBusy(client, cb), [client]),
    () => sceneBusy(client),
  );
}

const FAILED: Record<Exclude<SceneBusy, null>, string> = {
  save: "Couldn't save the scene file",
  open: "Couldn't open that scene file",
  ring: "Couldn't save the ring with its scene",
};

/**
 * Save scene file · Open scene file · Ring + scene — one set of actions for
 * every place that offers them (Basic › Export, Advanced › Output, the
 * Exports panel, ⌘K), sharing one busy flag and reporting by toast, so a
 * palette that closes the moment it runs still says what happened.
 *
 * `available` is caps `sceneFileV65`: false, and no caller shows them —
 * the four methods do not exist on an older viewer and RPC has no timeout.
 */
export function useSceneFileActions() {
  const { client } = useViewer();
  const store = useEditorStore();
  const caps = useCaps();
  const { toast } = useToast();
  const models = useDomainState("models");
  const busy = useSceneBusy();
  const name = models[0]?.name;

  const run = useCallback(
    async (what: Exclude<SceneBusy, null>, job: () => Promise<SceneMessage>) => {
      if (!caps.sceneFileV65 || sceneBusy(client)) return;
      try {
        toast(await withSceneBusy(client, what, job));
      } catch (e) {
        toast({ tone: "error", title: FAILED[what], description: e instanceof Error ? e.message : String(e) });
      }
    },
    [caps.sceneFileV65, client, toast],
  );

  const save = useCallback(
    () =>
      run("save", async () => {
        const f = await saveSceneFile(client, store, name);
        return { tone: "success", title: "Scene file saved", description: `${f.filename} · ${formatBytes(f.bytes)}` };
      }),
    [run, client, store, name],
  );

  /** Open a file already in hand (a drop, Replace model). */
  const openFile = useCallback(
    (file: File) => run("open", async () => describeImport(await openSceneFile(client, store, file))),
    [run, client, store],
  );

  // The dialog opens straight from the click (a browser only allows that
  // inside the user's gesture); the work runs once a file is picked.
  const open = useCallback(() => {
    if (!caps.sceneFileV65 || sceneBusy(client)) return Promise.resolve(false);
    return pickFile(SCENE_FILE_ACCEPT, openFile);
  }, [caps.sceneFileV65, client, openFile]);

  const ringWithScene = useCallback(
    () =>
      run("ring", async () => {
        const f = await exportRingWithScene(client, store, name);
        return { tone: "success", title: "Ring + scene saved", description: `${f.filename} · ${formatBytes(f.bytes)}` };
      }),
    [run, client, store, name],
  );

  return { available: caps.sceneFileV65, hasModel: models.length > 0, busy, save, open, openFile, ringWithScene };
}
